import * as React from "react";
import {
  createFileRoute,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { useRouter } from "@tanstack/react-router";
import { AppTopbar } from "@/components/app-topbar";
import { AuthedFooter } from "@/components/authed-footer";
import { VerifyEmailBanner } from "@/components/verify-email-banner";
import { AgentChatPanel } from "@/components/agent-chat-panel";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { isAuthFailure, handleAuthFailure } from "@/lib/auth-recovery";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/use-auth";
import { getMyProfile, updateMyProfile } from "@/lib/account.functions";
import { browserTimeZone, isValidTimeZone } from "@/lib/timezones";

/**
 * Last line of defence for anything that escapes a page's own error handling.
 * Expired sessions are handed to the shared recovery (which navigates to
 * sign in); everything else gets plain copy instead of raw error text.
 */
function AuthedErrorComponent({ error }: { error: unknown }) {
  const router = useRouter();
  const authFailure = isAuthFailure(error);  React.useEffect(() => {
    if (authFailure) handleAuthFailure();
  }, [authFailure]);
  if (authFailure) return null;
  return (
    <div className="mx-auto max-w-md px-4 py-16 text-center">
      <p className="text-sm text-muted-foreground">
        Something went wrong loading this page.
      </p>
      <Button
        className="mt-4"
        variant="outline"
        onClick={() => void router.invalidate()}
      >
        Try again
      </Button>
    </div>
  );
}

export const Route = createFileRoute("/_authed")({
  errorComponent: AuthedErrorComponent,
  beforeLoad: async ({ location }) => {
    if (typeof window === "undefined") return;
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      throw redirect({
        to: "/auth",
        search: { mode: "signin", redirect: location.href },
      });
    }
  },
  component: AppLayout,
});

/**
 * Saves the browser's own timezone once, quietly, for anyone who never set
 * one. Times in emails and invites then match the person's own clock.
 */
function useSilentTimezoneSave() {
  const { user } = useAuth();
  const fetchProfile = useServerFn(getMyProfile);
  const save = useServerFn(updateMyProfile);
  const profileQuery = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: () => fetchProfile(),
    enabled: !!user,
    staleTime: 5 * 60_000,
  });
  const savedOnce = React.useRef(false);
  React.useEffect(() => {
    if (savedOnce.current) return;
    const profile = profileQuery.data;
    if (!profile || profile.timezone) return;
    const zone = browserTimeZone();
    if (!zone || !isValidTimeZone(zone)) return;
    savedOnce.current = true;
    void save({ data: { timezone: zone } }).catch(() => {
      /* a timezone guess must never interrupt the page */
    });
  }, [profileQuery.data, save]);
}

function AppLayout() {
  useSilentTimezoneSave();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onAgentPage = pathname === "/agent" || pathname.startsWith("/agent/");
  return (
    <div className="flex min-h-screen flex-col bg-brand-paper-2">
      <AppTopbar />
      <VerifyEmailBanner />
      <div className="flex-1"><Outlet /></div>
      <AuthedFooter />
      {!onAgentPage && <AgentChatPanel variant="floating" />}
    </div>
  );
}
